/**
 * Persistent reconciliation orders ("corrections").
 *
 * Background
 * ----------
 * WHB's `/trading-history` endpoint filters out money-market trades
 * (SGOV and likely other short-term-treasury ETFs). This means our
 * cash-reconstruction engine can't see every trade that actually
 * moved cash in the Alpaca account — result: drift between the
 * reconstructed cash balance and Alpaca's ground-truth cash.
 *
 * Ideally WHB fixes the backend. Until then we compensate with a
 * synthetic Order carrying `orderType: "correction"` that closes
 * the remaining gap. The correction:
 *
 *   - Has a real cash total (positive for implied sells, negative
 *     for implied buys in WHB's direction — encoded by side).
 *   - Is fed into the reconstruction like any other order, so the
 *     daily equity curve includes it.
 *   - Shows up in the Orders tab with a distinct badge so the user
 *     knows it is not a broker row.
 *   - Persists in IndexedDB keyed by accountId. Survives sign-out
 *     (the auth cacheClear is explicitly scoped to skip the
 *     `corrections:` prefix) so the reconciliation follows the
 *     account, not the session.
 *
 * Design choices
 * --------------
 * Today we store ONE correction per account, recomputed each load
 * from `portfolio.cash − reconstructed_final_cash`. On subsequent
 * loads the correction's amount may move by a few dollars as market
 * data / dividends / fees accrue — that's fine; we upsert the single
 * entry rather than appending new corrections each refresh.
 *
 * A future richer model could split the correction by inferred
 * symbol (e.g. one SGOV correction, one for the IT price bug), or
 * allow user-entered manual corrections. The storage format below is
 * forward-compatible: `Order[]` keyed by a stable id, same shape as
 * real orders.
 */

import type { Order } from "@/data/mock-orders";
import { cacheGet, cacheKey, cacheSet } from "@/lib/whb-cache";

/** Threshold below which we don't bother emitting a correction. $0.50
 *  is smaller than any real Alpaca fee rounding drift, so anything
 *  bigger is signal worth surfacing. */
const MIN_CORRECTION_ABS = 0.5;

/** Stable id for "the SGOV-gap correction on this account". Using a
 *  deterministic id means upserts work correctly across reloads
 *  without accumulating stale rows. */
function sgovCorrectionId(accountId: string): string {
  return `correction:sgov:${accountId}`;
}

/** Read every persisted correction Order for the account. Returns an
 *  empty array if nothing is stored (or IndexedDB is unavailable). */
export async function loadCorrections(accountId: string): Promise<Order[]> {
  const hit = await cacheGet<Order[]>(cacheKey.corrections(accountId));
  if (!hit || !Array.isArray(hit.value)) return [];
  // Defensively filter — disk could hold rows from an older schema.
  return hit.value.filter(
    (o): o is Order =>
      !!o && typeof o === "object" && typeof o.id === "string" && o.orderType === "correction"
  );
}

/** Overwrite the full correction list for this account. */
export async function saveCorrections(
  accountId: string,
  corrections: readonly Order[]
): Promise<void> {
  await cacheSet(cacheKey.corrections(accountId), corrections);
}

/**
 * Upsert the single "SGOV gap" correction for an account given the
 * required cash shift. `diff > 0` means the reconstruction has LESS
 * cash than Alpaca (missing sells → book a synthetic sell). `diff < 0`
 * means MORE cash than Alpaca (missing buys → book a synthetic buy).
 *
 * The correction is dated `asOfIso` (caller passes today's date) and
 * stamped with a synthetic asset symbol SGOV so the Orders tab shows
 * it under the most likely root-cause ticker. Returns the updated
 * `Order[]` list (what's in the cache after the upsert).
 */
export async function upsertSgovGapCorrection(params: {
  accountId: string;
  /** Cash gap = portfolio.cash − reconstructed_final_cash_without_corrections. */
  diff: number;
  asOfIso: string;
}): Promise<Order[]> {
  const { accountId, diff, asOfIso } = params;
  const existing = await loadCorrections(accountId);
  // Strip any prior SGOV correction so we don't double-apply; we'll
  // re-insert the fresh one below if it's still material.
  const id = sgovCorrectionId(accountId);
  const others = existing.filter((o) => o.id !== id);

  if (Math.abs(diff) < MIN_CORRECTION_ABS) {
    // Below threshold — just drop any stale SGOV correction. Any
    // non-SGOV user corrections stay.
    await saveCorrections(accountId, others);
    return others;
  }

  // Sign convention: positive diff ⇒ we need to ADD cash to the
  // reconstruction ⇒ synthetic SELL (sell adds cash). Negative diff
  // ⇒ need to REMOVE cash ⇒ synthetic BUY.
  const side: Order["side"] = diff > 0 ? "sell" : "buy";
  const total = Math.abs(diff);
  // Fake 1 share at the full total — keeps qty × price = total exact.
  const qty = 1;
  const price = +total.toFixed(4);

  const correction: Order = {
    id,
    accountId,
    asset: "SGOV",
    assetName: "Missing SGOV trades (reconstruction correction)",
    side,
    orderType: "correction",
    filledQty: qty,
    avgFillPrice: price,
    totalAmount: +total.toFixed(2),
    status: "filled",
    source: "manual",
    submittedAt: asOfIso,
    filledAt: asOfIso,
    stateDetail:
      "Synthetic correction — WHB /trading-history filters SGOV trades. " +
      "Remove when backend SGOV filter is fixed.",
  };

  const next = [...others, correction];
  await saveCorrections(accountId, next);
  return next;
}
